/**
 * View component for /evaluations/:evaluationId
 *
 * Displays a single evaluation from the 'byId' map in the evaluation reducer
 * as defined by the 'selected' property
 *
 * NOTE: User MUST have a correct radio answer or all ONLY correct checkbox answers
 * to progress to the next question
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import third-party libraries
import classNames from 'classnames';
import renderHTML from 'react-render-html';

// import actions
import * as answerActions from '../../answer/answerActions';
import * as courseActions from '../../course/courseActions';
import * as evaluationActions from '../evaluationActions';
import * as questionActions from '../../question/questionActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';
import * as userAnswerActions from '../../userAnswer/userAnswerActions';
import * as userEvaluationActions from '../../userEvaluation/userEvaluationActions';

// import global components
import Base from "../../../global/components/BaseComponent.js.jsx";
import Modal from '../../../global/components/modals/Modal.js.jsx';
import VideoPlayer from "../../../global/components/helpers/VideoPlayer.js.jsx";

// import module components
import AnswerCheckboxes from '../../answer/components/AnswerCheckboxes.js.jsx';
import AnswerRadios from '../../answer/components/AnswerRadios.js.jsx';
import EvaluationLayout from '../components/EvaluationLayout.js.jsx';
import QuestionVideo from '../../question/components/QuestionVideo.js.jsx';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';
import videoUtils from "../../../global/utils/videoUtils.js";

// import evaluation css modules
import evaluationStyles from '../evaluationModuleStyles.css';

class EvaluationQuestion extends Base {
  constructor(props) {
    super(props);
    this.state = {
      allSelectedAnswerIds: []
      , answerVidModalOpen: false
      , isCorrect: false
      , player: {}
      , selectedAnswerId: ''
      , userAnswersByAnswerId: {} // Map of answerId -> userAnswer object from server
    }
    this._bind(
      '_checkAllCorrectFromUserAnswers'
      , '_closeModal'
      , '_finishEvaluation'
      , '_goToNextQuestion'
      , '_handleCheckAnswer'
      , '_handleReady'
      , '_handleSelectRadio'
      , '_handleUncheckAnswer'
      , '_onAnswerVideoComplete'
    )
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(answerActions.fetchListIfNeeded('_question', match.params.questionId));
    if(match.params.courseSlug) {
      // non-agenda route.  useful to have course info
      dispatch(courseActions.fetchSingleBySlug(match.params.courseSlug));
    } else {
      // want to make sure the old course info doesn't persist on agenda routes
      dispatch(courseActions.invalidateSelected())
    }
    dispatch(evaluationActions.fetchSingleIfNeeded(match.params.evaluationId)).then(action1 => {
      // fetch UserEvaluation object
      dispatch(userEvaluationActions.fetchSingleUserEvaluationByEvaluation(action1.item._id)).then(action2 => {
        if(!action2.success) {
          alert("Error finding current User Evaluation");
        } else if(!action2.item) {
          // since these objects are created elsewhere, if it DOESN'T exist we need to redirect the user (if they copied the url from someone else, for example)
          // TODO: this should redirect silently when we go live.
          alert("ATTN: You must first start the Module Evaluation.");
          this.props.history.push(`${match.url.replace('evaluations/' + action1.item._id, 'conclusion')}`)
        } else {
          // fetch the user's previous answers in this session, if they exist.
          // NOTE: how does that work? like if they go back again after???

        }
      })
    })
    dispatch(questionActions.fetchListIfNeeded('_evaluation', match.params.evaluationId));
    dispatch(questionActions.fetchSingleIfNeeded(match.params.questionId));
    // dispatch(contentModuleActions.fetchSingleBySlug(match.params.contentModuleSlug));
  }

  componentWillReceiveProps(nextProps) {
    const { dispatch, match } = nextProps;
    if(nextProps.questionStore.selected.invalid || nextProps.match.params.questionId !== this.props.match.params.questionId) {
      this.props.dispatch(answerActions.fetchListIfNeeded('_question', nextProps.match.params.questionId));
      this.props.dispatch(questionActions.fetchSingleIfNeeded(nextProps.match.params.questionId));
      this.setState({
        allSelectedAnswerIds: []
        , answerVidModalOpen: false
        , isCorrect: false
        , selectedAnswerId: ''
        , userAnswersByAnswerId: {}
      })
    }
  }

  _checkAllCorrectFromUserAnswers(selectedAnswerIds, userAnswersByAnswerId) {
    /**
     * SECURITY: This method uses userAnswer.correct and questionComplete from the server instead of answer.correct
     *
     * For checkbox questions, we check if:
     * 1. All selected answers have corresponding userAnswers (API calls completed)
     * 2. The most recent userAnswer has questionComplete === true (all correct answers found)
     *
     * For radio questions, we just check if the answer is correct.
     */
    if(!selectedAnswerIds || selectedAnswerIds.length === 0) {
      return false;
    }

    // Get the most recent userAnswer (the last one added)
    const lastAnswerId = selectedAnswerIds[selectedAnswerIds.length - 1];
    const lastUserAnswer = userAnswersByAnswerId[lastAnswerId];

    if(!lastUserAnswer) {
      // API call hasn't completed yet
      return false;
    }

    // Use questionComplete flag from server (handles both radio and checkbox correctly)
    return lastUserAnswer.questionComplete === true;
  }

  _finishEvaluation() {
    const { dispatch, history, match, questionStore, userEvaluationStore } = this.props;
    const userEvaluation = userEvaluationStore.selected.getItem();

    dispatch(userEvaluationActions.sendGradeUserEvaluation(userEvaluation)).then(action => {
      history.push(match.url.replace(`q/${match.params.questionId}`, 'post-evaluation'));
    })

  }

  _handleReady(playerId) {
    // console.log('player ready');
    const player = window.jwplayer(playerId);
    // console.log(player);
    // player.play();
    this.setState({player})
  }

  _goToNextQuestion() {
    // console.log('_goToNextQuestion')
    const { dispatch, history, match, questionStore, userEvaluationStore } = this.props;
    const userEvaluation = userEvaluationStore.selected.getItem();
    const questionList = questionStore.util.getList('_evaluation', match.params.evaluationId);
    const sortedQuestions = filterUtils.sortByOrderWeight(questionList);
    const questionIndex = _.findIndex(sortedQuestions, {_id: questionStore.selected.id})
    dispatch(questionActions.invalidateSelected())
    if(questionIndex === sortedQuestions.length - 1) {
      this._finishEvaluation();
      // history.push(match.url.replace(`q/${match.params.questionId}`, 'post-evaluation'));
    } else {
      // save next question as "last viewed" so that user can continue from their if they close the page or something
      dispatch(userEvaluationActions.sendUpdateUserEvaluation({...userEvaluation, _lastQuestion: sortedQuestions[questionIndex + 1]._id}))
      history.push(match.url.replace(match.params.questionId, sortedQuestions[questionIndex + 1]._id));
    }
  }

  _onAnswerVideoComplete(e) {
    console.log('answer video done');
    console.log(e);
  }

  _closeModal() {
    this.setState({answerVidModalOpen: false})
  }

  _handleCheckAnswer(answerId) {
    // console.log('_handleCheckAnswer');
    if(this.state.player && this.state.player.getState && this.state.player.stop) {
      setTimeout(() => {
        // contrary to their readme, calling pause twice will PLAY the video
        // this.state.player.pause();
        this.state.player.stop();
      }, 250)
    }
    const { answerStore, dispatch, userEvaluationStore } = this.props;
    const answer = answerStore.byId[answerId];
    const userEvaluation = userEvaluationStore.selected.getItem();
    let ids = this.state.allSelectedAnswerIds;
    ids.push(answerId);

    this.setState({
      allSelectedAnswerIds: ids
      , answerVidModalOpen: true
      , selectedAnswerId: answerId
    })

    let userAnswer = {
      _answer: answer._id
      , _evaluation: answer._evaluation
      , _question: answer._question
      , _contentModule: answer._contentModule
      , _userEvaluation: userEvaluation._id
      , _userModule: userEvaluation._userModule
      // SECURITY: Do not send 'correct' from client - server will calculate it
    }

    // Send to server and store the returned userAnswer
    dispatch(userAnswerActions.sendCreateUserAnswer(userAnswer)).then(action => {
      if(action.success && action.item) {
        const newUserAnswers = {...this.state.userAnswersByAnswerId};
        newUserAnswers[answerId] = action.item;

        // Check if all selected answers are correct (for checkboxes)
        const allAnswersCorrect = this._checkAllCorrectFromUserAnswers(ids, newUserAnswers);

        this.setState({
          userAnswersByAnswerId: newUserAnswers
          , isCorrect: allAnswersCorrect
        });
      }
    });
  }

  _handleUncheckAnswer(answerId) {
    // console.log('handleUncheckAnswer');
    let ids = this.state.allSelectedAnswerIds;
    ids.splice(ids.indexOf(answerId), 1);
    const allAnswersCorrect = this._checkAllCorrectFromUserAnswers(ids, this.state.userAnswersByAnswerId);
    this.setState({
      allSelectedAnswerIds: ids
      , isCorrect: allAnswersCorrect
      , selectedAnswerId: ''
    })
  }

  _handleSelectRadio(answerId) {
    // console.log("_handleSelectRadio")
    // console.log(this.state.player)
    // console.log(this.state.player && this.state.player.getState ? this.state.player.getState() : "n/a")

    /**
     * halting bug with "getState" under certain conditions. to replicate: start on a question A, with a video. answer and go to next question B without a video. click any answer you and get a null error from the getState method.
     * the changes may result in multiple pauses being called, but in practice it doesn't seem to have a negative effect.
     */
    // if(this.state.player && this.state.player.getState && this.state.player.getState() === "playing") {
    if(this.state.player && this.state.player.getState && this.state.player.stop) {
      setTimeout(() => {
        // contrary to their readme, calling pause twice will PLAY the video
        // this.state.player.pause();
        this.state.player.stop();
      }, 250)
    }
    const { answerStore, dispatch, userEvaluationStore } = this.props;
    const answer = answerStore.byId[answerId];
    const userEvaluation = userEvaluationStore.selected.getItem();

    this.setState({
      answerVidModalOpen: true
      , selectedAnswerId: answerId
    })

    let userAnswer = {
      _answer: answer._id
      , _evaluation: answer._evaluation
      , _question: answer._question
      , _contentModule: answer._contentModule
      , _userEvaluation: userEvaluation._id
      , _userModule: userEvaluation._userModule
      // SECURITY: Do not send 'correct' from client - server will calculate it
    }

    // Send to server and store the returned userAnswer
    dispatch(userAnswerActions.sendCreateUserAnswer(userAnswer)).then(action => {
      if(action.success && action.item) {
        const newUserAnswers = {...this.state.userAnswersByAnswerId};
        newUserAnswers[answerId] = action.item;

        this.setState({
          userAnswersByAnswerId: newUserAnswers
          , isCorrect: action.item.questionComplete === true
        });
      }
    });
  }

  render() {
    // console.log(this.state);
    const {
      answerStore
      , courseStore
      , defaultQuestion
      , evaluationStore
      , loggedInUser
      , match
      , questionStore
      , contentModuleStore
      , userAnswerStore
    } = this.props;
    const {
      allSelectedAnswerIds
      , answerVidModalOpen
      , isCorrect
      , selectedAnswerId
      // , sortedAnswers
    } = this.state;
    /**
     * use the selected.getItem() utility to pull the actual evaluation object from the map
     */
    const selectedEvaluation = evaluationStore.selected.getItem();
    const selectedQuestion = questionStore.selected.getItem();
    const answerList = answerStore.util.getList('_question', match.params.questionId);
    const questionList = questionStore.util.getList('_evaluation', match.params.evaluationId);
    const isEmpty = (
      !selectedEvaluation
      || !selectedEvaluation._id
      || !selectedQuestion
      || !selectedQuestion._id
      || evaluationStore.selected.didInvalidate
      // || courseStore.selected.isFetching
      // || !courseStore.selected.id
      || !questionList
      || !answerList
      || answerList.length < 1
    );
    // console.log('debug 1');
    // let questionVidSources;
    // let sortedAnswers;
    let sortedQuestions;
    let questionIndex;
    if(!isEmpty) {
      // questionVidSources = videoUtils.returnVideoSources(selectedQuestion.videoUrl, selectedQuestion.posterUrl, false, selectedQuestion._id);
      sortedQuestions = filterUtils.sortByOrderWeight(questionList);
      questionIndex = _.findIndex(sortedQuestions, {_id: selectedQuestion._id})
      // sortedAnswers = selectedQuestion.randomizeAnswerOrder ? filterUtils.randomizeOrder(answerList) : filterUtils.sortByOrderWeight(answerList);
      // this._setSortedAnswers(answerList, selectedQuestion);
    }
    let nextQuestionBtn = classNames(
      'yt-btn -next-btn'
    )
    return (
      <EvaluationLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              {isEmpty ?
                (questionStore.selected.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
                :
                <div style={{ opacity: questionStore.selected.isFetching ? 0.5 : 1 }}>
                  <div className="yt-row center-horiz">
                    <div className="yt-col full s_80 m_75 l_60 xl_50">
                      <div style={{marginBottom: '10px', marginTop: '10px'}} className="-buttons">
                        <a href={`${window.agendaUrl}/agenda`} className="yt-btn -exit-btn">
                          <i className="fas fa-arrow-left" style={{marginRight: '10px'}}/>
                          Exit to Agenda
                        </a>
                      </div>
                      <h3 className="-q-index"> Question {questionIndex + 1} of {sortedQuestions.length}</h3>
                        { selectedQuestion.videoPath ?
                          <QuestionVideo
                            autoPlay={true}
                            evaluationId={selectedEvaluation._id}
                            courseId={courseStore.selected.id}
                            handleReady={this._handleReady}
                            question={selectedQuestion}
                            contentModuleId={contentModuleStore.selected.id}
                          />
                          : selectedQuestion.posterPath ?
                          <img src={videoUtils.returnPosterUrl(selectedQuestion.posterPath)} />
                          :
                          null
                        }

                      <div className="content-container user-question">
                        <div className="-q-content">{questionIndex + 1}. {selectedQuestion.content}</div>
                        <div className="-answers">
                          { selectedQuestion.answerType === 'single' ?
                            <AnswerRadios
                              answers={answerList}
                              changeAnswer={this._handleSelectRadio}
                              loggedInUser={loggedInUser}
                              questionId={selectedQuestion._id}
                              randomizeOrder={selectedQuestion.randomizeAnswerOrder}
                              selectedAnswerId={this.state.selectedAnswerId}
                              userAnswersByAnswerId={this.state.userAnswersByAnswerId}
                            />
                            : selectedQuestion.answerType === 'multiple' ?
                            <AnswerCheckboxes
                              answers={answerList}
                              handleCheckAnswer={this._handleCheckAnswer}
                              handleUncheckAnswer={this._handleUncheckAnswer}
                              loggedInUser={loggedInUser}
                              questionId={selectedQuestion._id}
                              randomizeOrder={selectedQuestion.randomizeAnswerOrder}
                              selectedAnswers={allSelectedAnswerIds}
                              userAnswersByAnswerId={this.state.userAnswersByAnswerId}
                            />
                            :
                            <div>text area here</div>
                          }
                        </div>
                        { questionIndex === sortedQuestions.length - 1 ?
                          <button
                            className={nextQuestionBtn}
                            disabled={!isCorrect}
                            onClick={this._finishEvaluation}
                          >
                            <span>Finish Exam <i className="fa fa-arrow-right"/></span>
                          </button>
                          :
                          <div className="-buttons">
                          <button
                            className={nextQuestionBtn}
                            disabled={!isCorrect}
                            onClick={this._goToNextQuestion}
                          >
                            <span>Next Question <i className="fa fa-arrow-right"/></span>
                          </button>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                  <Modal
                    btnColor={selectedAnswerId.length > 0 && this.state.userAnswersByAnswerId[selectedAnswerId] && this.state.userAnswersByAnswerId[selectedAnswerId].correct ? 'success' : 'danger'}
                    cardSize='standard'
                    closeAction={this._closeModal}
                    confirmAction={this._closeModal}
                    confirmText="Got it"
                    isOpen={(answerVidModalOpen && selectedAnswerId.length > 0)}
                    modalClasses={selectedAnswerId && this.state.userAnswersByAnswerId[selectedAnswerId] && this.state.userAnswersByAnswerId[selectedAnswerId].correct ? 'success' : 'danger'}
                    modalHeader={selectedAnswerId && this.state.userAnswersByAnswerId[selectedAnswerId] && this.state.userAnswersByAnswerId[selectedAnswerId].correct ? 'Correct' : 'Incorrect'}
                    showButtons={true}
                    showClose={false}
                  >
                    <VideoPlayer
                      adminPreview={false}
                      isAutoPlay={true}
                      onVideoComplete={() => console.log('done playing')}
                      playerId={selectedAnswerId}
                      playlist={!selectedAnswerId ? null : videoUtils.returnPlaylist(answerStore.byId[selectedAnswerId].videoPath, answerStore.byId[selectedAnswerId].posterPath, answerStore.byId[selectedAnswerId].tracks)}
                      tracking={{
                        _answer: selectedAnswerId
                        , _course: courseStore.selected.id
                        , _evaluation: this.props.match.params.evaluationId
                        , _question: this.props.match.params.questionId
                        , _contentModule: selectedEvaluation._contentModule
                        , description: "user-answer"
                      }}
                    />
                  </Modal>
                </div>
              }
            </div>
          </section>
        </div>
      </EvaluationLayout>
    )
  }
}


EvaluationQuestion.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store, ownProps) => {
  /**
   * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
   * differentiated from the React component's internal state
   */

  return {
    answerStore: store.answer
    , courseStore: store.course
    , defaultQuestion: store.question.defaultItem
    , evaluationStore: store.evaluation
    , loggedInUser: store.user.loggedIn.user
    , questionStore: store.question
    , contentModuleStore: store.contentModule
    , userAnswerStore: store.userAnswer
    , userEvaluationStore: store.userEvaluation
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(EvaluationQuestion)
);



// WEBPACK FOOTER //
// modules/evaluation/views/EvaluationQuestion.js.jsx